/**
 * The machine-readable reasons a request was refused.
 *
 * An `ErrorCode` says which *kind* of refusal happened — 409, 403, 402 — and a
 * message says which one in English. Neither is enough for a Khmer merchant: a
 * confirm dialog translated into Khmer that answers "Supplies carry this
 * brand." has told them the delete failed in a language they do not read. So a
 * throw may also carry a reason, the client looks up `error.<reason>` in the
 * pack it is already holding, and the sentence arrives in the merchant's own
 * language.
 *
 * **A reason is a wire contract**, on the same footing as a route path or a
 * permission key: renaming one is an amendment, not a rename. It is deliberately
 * *not* derived from the message — two throws that say the same thing to a
 * person share a reason, and a message reworded in English keeps its reason.
 *
 * **Not every throw has one, on purpose.** The API throws 474 times; the ~80
 * named here are the ones a merchant or a cashier actually meets. Everything
 * else — every `INTERNAL`, every "No such X." that only a broken client can
 * provoke, and everything only an operator sees — falls back to the server's
 * English exactly as it does today, which is the right answer for a reader who
 * is debugging rather than working. `error-reasons.test.ts` records which route
 * files were keyed and which were left to the fallback, with the reason.
 *
 * Adding one is three steps: name it here, throw it with `.because()`, and
 * write the sentence in all four packs. The third is enforced by the
 * typechecker — `t(\`error.${reason}\`)` in `resolveErrorMessage` is only
 * assignable if every reason has a key — and by `error-reasons.test.ts`, which
 * also catches a reason thrown but never declared.
 */
export const ERROR_REASONS = [
  // The five gates of `07-entitlements-rbac.md`, which every tenant endpoint
  // passes through. `forbidden`, `notEntitled` and `quotaExceeded` reuse the
  // keys the packs already carry for exactly these sentences.
  "forbidden",
  "notEntitled",
  "quotaExceeded",
  "inventoryForbidden",
  "capabilityNotDelegated",
  "rateLimited",

  // Getting in, and staying in. A cashier meets most of these on a terminal
  // that has no English on it anywhere else.
  "signInRequired",
  "accountDisabled",
  "accountNoStore",
  "storeNotAllowed",
  "pinRequired",
  "pinSessionExpired",
  "pinMismatch",
  "terminalRequired",
  "grantNotRecognised",
  "deviceCodeNotPending",
  "alreadyClockedIn",
  "notClockedIn",
  "clockedInElsewhere",

  // A row that will not delete because something still points at it. These are
  // the confirm-dialog refusals: the merchant has already said yes.
  "unitInUse",
  "unitSeeded",
  "unitSelfConversion",
  "unitDimensionMismatch",
  "supplyCategoryHasChildren",
  "supplyCategoryInUse",
  "supplyCategorySelfParent",
  "supplyCategoryTooDeep",
  "brandInUse",
  "supplyHasStock",
  "supplyNumberTaken",
  "barcodeTaken",
  "menuCategoryInUse",
  "itemNumberTaken",
  "tableAreaInUse",
  "stationHasPrinters",
  "stationHasItems",
  "printerHasItems",
  "printerNeedsAddress",
  "printerNeedsStation",
  "paymentMethodBuiltIn",
  "tipPoolPaidOut",

  // The bill: settling it, refunding it, reversing it. `store/orders.ts` is
  // where a till spends its day and where a refusal costs a queue.
  "tableAlreadyHasBill",
  "billAlreadySettled",
  "billNotCheckedOut",
  "orderCancelled",
  "settledBillNeedsReverse",
  "moneyTakenUseRefund",
  "nothingTakenYet",
  "nothingLeftToRefund",
  "refundExceedsTaken",
  "paymentExceedsOutstanding",
  "paymentMethodUnavailable",
  "paymentProviderNotInstalled",
  "paymentRailPending",
  "paymentAsyncUnsupported",
  "discountExceedsBill",
  "returnExceedsOrdered",
  "takingsCounted",
  "tipNotSupportedOnTender",
  "mergeIntoSelf",
  "mergeSourcePaid",
  "splitNoSeats",
  "splitSingleSeat",
  "splitLeavesEmptyBill",
  "courseNothingWaiting",

  // Tenders that are not cash: a card, a balance, a tab.
  "giftCardCodeRequired",
  "giftCardVoided",
  "giftCardExpired",
  "giftCardCurrencyMismatch",
  "giftCardInsufficient",
  "memberRequired",
  "memberBlocked",
  "memberBalanceInsufficient",
  "memberPointsInsufficient",
  "tabClosed",
  "tabNothingLeft",
  "tabRepayExceeds",
  "tabRepaidWithTab",

  // Coupons and promotions, which a cashier applies at the till and a manager
  // issues from the back office.
  "couponSwitchedOff",
  "couponUsed",
  "couponExpired",
  "couponNotYetValid",
  "couponOtherMember",
  "couponBillEmpty",
  "couponBelowMinimum",

  // Ordering from a table's code, which a guest reads on their own phone: the
  // geofence `routes/diner.ts` holds a QR order to.
  "orderLocationRequired",
  "orderTooFar",

  // The day itself.
  "shiftAlreadyOpen",
  "shiftAlreadyClosed",
  "noShiftOpen",
  "shiftTenderNothingTaken",

  // Role editing itself: a store cannot be left with nobody who can grant
  // `roles:write` back.
  "lastRoleEditor",

  // An employee's photo: `store/staff.ts`'s `PUT .../photo`.
  "photoNotJpeg",
  "photoTooLarge",

  // Setting an employee's staff-app password, and the role a manager may hand
  // out while doing it: `store/staff.ts`'s `POST .../password`, `POST /staff`
  // and `PATCH /staff/:id`.
  "passwordForSelf",
  "employeeOutranksYou",
  "roleOutranksYou",
] as const;

export type ErrorReason = (typeof ERROR_REASONS)[number];

const KNOWN = new Set<string>(ERROR_REASONS);

/**
 * Whether a value off the wire is a reason this build knows.
 *
 * The client checks rather than casts, because a reason it does not know is
 * one it cannot translate: a newer API naming `couponSuspended` to an older
 * back office must fall back to the server's English, not print
 * `error.couponSuspended` at a merchant.
 */
export function isErrorReason(value: unknown): value is ErrorReason {
  return typeof value === "string" && KNOWN.has(value);
}
